import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular-boost';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, filter, map, startWith } from 'rxjs/operators';
import {
  IAudit,
  IInvoice,
  IOrganization,
  IPermission,
  IProfile,
  DriveType,
  IDrive,
} from '../interfaces/graph-types';

type ErrorCallback = (err: string) => any;
type SuccessCallback = (data: any) => any;

@Injectable()
export class OrganizationService {
  private _organization: BehaviorSubject<IOrganization>;
  loading: boolean = true;
  res: any;

  // ------------------------------------------------------
  constructor(private apollo: Apollo) {
    this._organization = new BehaviorSubject<IOrganization>(null);
  }

  // ------------------------------------------------------
  public queryGraphQL(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQLPoll(query, variables, pollInterval): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
      pollInterval: pollInterval,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQLNoCache(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'no-cache', // 'network-only' 'cache-and-network', // 'no-cache', //   -> https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-config-options-fetchPolicy
      notifyOnNetworkStatusChange: false,
    }).valueChanges; //.pipe(take(1));
  }

  // ------------------------------------------------------
  public getId(): string {
    return this._organization.value.id;
  }

  // ------------------------------------------------------
  public setOrganizationId(organizationId: string) {
    if (organizationId) {
      this.getOrganizationInfo(organizationId).subscribe(response => {
        this._organization.next(response);
      });
    }
  }

  // ------------------------------------------------------
  public organization(): BehaviorSubject<IOrganization> {
    return this._organization;
  }

  // ------------------------------------------------------
  public getOrganizationInfo(
    organizationId: string,
  ): Observable<IOrganization> {
    let variables = { organizationId };

    let query = gql`
      query getOrganizationInfo($organizationId: ID!) {
        organization(orgId: $organizationId) {
          id
          name
          domain
          orgtype
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.organization),
    );
  }

  // ------------------------------------------------------
  public getOrgs(): Observable<IProfile> {
    let variables = {};

    let query = gql`
      query profileOrgs {
        profile {
          id
          organizations {
            id
            name
            domain
            orgtype
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.profile),
    );
  }

  // ------------------------------------------------------
  public setField(
    organizationId: string,
    fieldName: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      organizationId: organizationId,
      fieldName: fieldName,
      value: value,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setOrganizationField($input: SetOrganizationFieldInput!) {
            setOrganizationField(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setOrganizationField;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public getAccountList(organizationId: string): Observable<IOrganization> {
    if (!organizationId) {
      console.log('!!! ERROR: getAccountList called with 0 !!!');
    }

    return this.apollo
      .watchQuery<{ organization: IOrganization }>({
        variables: { organizationId },
        query: gql`
          query getAccountList($organizationId: ID!) {
            organization(orgId: $organizationId) {
              id
              accounts {
                id
                name
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.organization));
  }

  // ------------------------------------------------------
  public drives(
    organizationId: string,
    driveType: DriveType = null,
  ): Observable<IDrive[]> {
    let variables = { organizationId, driveType };

    let query = gql`
      query drives($organizationId: ID!, $driveType: String) {
        organization(orgId: $organizationId) {
          id
          drives(driveType: $driveType) {
            id
            name
            email
            accredited
            avatarUrl
            type
            sharing
            canView
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.organization.drives),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public settings(organizationId: string): Observable<IOrganization> {
    return this.apollo
      .watchQuery<{ organization: IOrganization }>({
        variables: { organizationId },
        query: gql`
          query orgSettings($organizationId: ID!) {
            organization(orgId: $organizationId) {
              id
              name
              emailReportDeliveryTest
              webhookUrl
              emailVerificationCredits
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.organization),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public accounts(organizationId: string): Observable<IOrganization> {
    if (!organizationId) {
      console.log('!!! ERROR: getorgaccountsinfo called with 0 !!!');
    }

    return this.apollo
      .watchQuery<{ organization: IOrganization }>({
        variables: { organizationId },
        query: gql`
          query getAccountStats($organizationId: ID!) {
            organization(orgId: $organizationId) {
              id
              accounts {
                id
                name
                chartist
                availableCandidates
                addedCandidates
                openRate
                plan
                inboxes {
                  id
                  name
                  email
                  accredited
                }
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.organization));
  }

  // ------------------------------------------------------
  public billing(orgId: string): Observable<IOrganization> {
    let variables = { orgId };

    let query = gql`
      query billing($orgId: ID!) {
        organization(orgId: $orgId) {
          id
          hidden
          orgtype
          billing {
            id
            ccCountry
            ccBrand
            ccExpMonth
            ccExpYear
            ccName
            ccLast4
            invoiceName
            invoiceCompanyName
            invoiceAddress1
            invoiceAddress2
            invoiceCity
            invoiceState
            invoiceZipCode
            invoiceCountry
            invoiceVat
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.organization),
    );
  }

  // ------------------------------------------------------
  public invoices(orgId: string): Observable<IInvoice[]> {
    if (!orgId) {
      console.error('!!! ERROR: getInboxes called with 0 !!!');
    }

    return this.apollo
      .watchQuery<{ organization: IOrganization }>({
        variables: { orgId: orgId },
        query: gql`
          query invoices($orgId: ID!) {
            organization(orgId: $orgId) {
              id
              invoices {
                id
                createdAt
                amountPaid
                description
                downloadLink
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.organization.invoices),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public audits(orgId: string, avatarSize: number): Observable<IAudit[]> {
    if (!orgId) {
      console.error('!!! ERROR: audits called with 0 !!!');
    }

    return this.apollo
      .watchQuery<{ organization: IOrganization }>({
        variables: { orgId: orgId },
        query: gql`
        query organizationChanges($orgId: ID!) {
          organization(orgId: $orgId) {
            id
            audits {
              id
              author {
                id
                name
                email
                avatarUrl(size: ${avatarSize})
              }
              name
              scope
              createdAt
              completedAt
              error
            }
          }
        }
      `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.organization.audits),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public teamMembers(orgId: string): Observable<IProfile[]> {
    if (!orgId) {
      console.error('!!! ERROR: audits called with 0 !!!');
    }
    let avatarSize = 64;

    return this.apollo
      .watchQuery<{ organization: IOrganization }>({
        variables: { orgId: orgId },
        query: gql`
        query organizationChanges($orgId: ID!) {
          organization(orgId: $orgId) {
            id
            teamMembers {
              id
              name
              email
              avatarUrl(size: ${avatarSize})
            }
          }
        }
      `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.organization.teamMembers),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public permissions(orgId: string): Observable<IPermission[]> {
    if (!orgId) {
      console.error('!!! ERROR: audits called with 0 !!!');
    }
    let avatarSize = 64;

    // return this.apollo
    //   .watchQuery<{ organization: IOrganization }>({
    //     variables: { orgId: orgId },
    //     query: gql`
    //     query organizationPermission($orgId: ID!) {
    //       organization(orgId: $orgId) {
    //         id
    //         permissions {
    //           id
    //           admin
    //           logon {
    //             id
    //             name
    //             email
    //             avatarUrl(size: ${avatarSize})
    //           }
    //         }
    //       }
    //     }
    //   `,
    //     fetchPolicy: 'no-cache',
    //     notifyOnNetworkStatusChange: false,
    //   })
    //   .valueChanges.pipe(
    //     map(({ data }) => data.organization.permissions),
    //     startWith(null),
    //   );

    let variables = { orgId };

    let query = gql`
      query organizationPermission($orgId: ID!) {
        organization(orgId: $orgId) {
          id
          permissions {
            id
            admin
            logon {
              id
              name
              email
              avatarUrl(size: ${avatarSize})
            }
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.organization.permissions),
    );
  }

  // ------------------------------------------------------
  public buyEmailVerificationCredits(
    orgId: string,
    accountId: string,
    quantity: number,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      orgId,
      accountId,
      quantity,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation buyEmailVerificationCredits(
            $input: BuyEmailVerificationCreditsInput!
          ) {
            buyEmailVerificationCredits(input: $input) {
              stripeConfirmationSecret
              stripePaymentMethod
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].buyEmailVerificationCredits;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public initiateSetupIntent(
    orgId: string,
    name: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      orgId,
      name,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation initiateSetupIntent($input: InitiateSetupIntentInput!) {
            initiateSetupIntent(input: $input) {
              secret
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].initiateSetupIntent;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public completeSetupIntent(
    orgId: string,
    paymentMethod: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      orgId,
      paymentMethod,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation completeSetupIntent($input: CompleteSetupIntentInput!) {
            completeSetupIntent(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].completeSetupIntent;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createAccount(
    orgId: string,
    name: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      name: name,
      orgId: orgId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createAccount($input: CreateAccountInput!) {
            createAccount(input: $input) {
              accountId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createAccount;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public generateTeamMemberInvite(
    orgId: string,
    note: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      orgId: orgId,
      note: note,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation generateTeamMemberInvite(
            $input: GenerateTeamMemberInviteInput!
          ) {
            generateTeamMemberInvite(input: $input) {
              link
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].generateTeamMemberInvite;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public removeTeamMember(
    orgId: string,
    memberId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      orgId: orgId,
      memberId: memberId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation removeTeamMember($input: RemoveTeamMemberInput!) {
            removeTeamMember(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].removeTeamMember;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setPermissionAdmin(
    orgId: string,
    permissionId: string,
    value: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = { orgId, permissionId, value };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setPermissionAdmin($input: SetPermissionAdminInput!) {
            setPermissionAdmin(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },

        update: (store, results) => {
          // { data: { setPermissionAdmin } }
          // We don't want to update the cache if result is an error

          if (results['data']['setPermissionAdmin']['error']) {
            return;
          }

          // Read the data from our cache for this query.
          let query = gql`
            query updateOrganizationPermission($orgId: ID!) {
              organization(orgId: $orgId) {
                id
                permissions {
                  id
                  admin
                }
              }
            }
          `;
          const cache = store.readQuery({
            query: query,
            variables: { orgId },
          });
          // update our flag status
          let perms = cache['organization']['permissions'];
          perms.forEach(perm => {
            if (perm.id == permissionId) {
              perm.admin = value;
            }
          });
          // Write our data back to the cache.
          store.writeQuery({ query: query, data: cache });
        },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setPermissionAdmin;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteOrganization(
    organizationId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      organizationId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteOrganization($input: DeleteOrganizationInput!) {
            deleteOrganization(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteOrganization;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteBilling(
    organizationId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      organizationId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteBilling($input: DeleteBillingInput!) {
            deleteBilling(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteBilling;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }
}
